import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { useEffect } from "react";
import { setTokenGetter } from "./lib/api";
import { OrgIdProvider } from "./hooks/useOrgId";
import Layout from "./components/Layout";
import CmsGate from "./components/CmsGate";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import Overview from "./pages/Overview";
import Articles from "./pages/Articles";
import Cannibalization from "./pages/Cannibalization";
import KnowledgeGraph from "./pages/KnowledgeGraph";
import Recommendations from "./pages/Recommendations";
import Linking from "./pages/Linking";
import Settings from "./pages/Settings";
import SiteSection from "./components/settings/SiteSection";
import BusinessSection from "./components/settings/BusinessSection";
import ApiKeysSection from "./components/settings/ApiKeysSection";
import IntegrationsSection from "./components/settings/IntegrationsSection";
import CmsListSection from "./components/settings/cms/CmsListSection";
import CmsDetailSection from "./components/settings/cms/CmsDetailSection";
import { SearchConsoleSection, IndexingSection } from "./components/settings/GscSections";
import Writing from "./pages/Writing";
import OutlineEditor from "./pages/OutlineEditor";
import SitemapPage from "./pages/SitemapPage";
import FanoutPage from "./pages/FanoutPage";
import QueryMapPage from "./pages/QueryMapPage";
import TechnicalAuditPage from "./pages/TechnicalAuditPage";
import AuditPageDetailPage from "./pages/AuditPageDetailPage";
import QueryGapsPage from "./pages/QueryGapsPage";
import QueryDetailPage from "./pages/QueryDetailPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import ArticleOutlinePage from "./pages/ArticleOutlinePage";
import SchemaAuditPage from "./pages/SchemaAuditPage";
import SearchPerformancePage from "./pages/SearchPerformancePage";
import AudiencePage from "./pages/AudiencePage";
import TasksPage from "./pages/TasksPage";
import AdminPage from "./pages/AdminPage";
import AgentSetupPage from "./pages/AgentSetupPage";
import ReportsPage from "./pages/ReportsPage";
import AiCitationsPage from "./pages/AiCitationsPage";
import ReportDetailPage from "./pages/ReportDetailPage";
import ReportTemplatesPage from "./pages/ReportTemplatesPage";
import WorkspacePage from "./pages/onboarding/WorkspacePage";
import "./App.css";

// Wires Clerk's getToken into the axios instance so every request carries
// the active org's JWT. Must render inside ClerkProvider.
function ApiTokenBridge() {
  const { getToken, orgId } = useAuth();
  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken, orgId]);
  return null;
}

const WORKSPACE_URL = "/onboarding/workspace";

function ClerkWithRouter({ children }) {
  const navigate = useNavigate();
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;


  return (
    <ClerkProvider
      publishableKey={publishableKey}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl="/"
      // Membership required: a session without an org is "pending" until the
      // choose-organization task is done on our workspace page.
      taskUrls={{ "choose-organization": WORKSPACE_URL }}
      appearance={{
        elements: {
          rootBox: "font-sans",
          card: "shadow-lg border border-sidebar-border",
        },
      }}
      // Force Clerk to use default CDN instead of any custom domain
      options={{
        proxyUrl: undefined,
        domain: undefined,
        publishableKey: publishableKey,
      }}
    >
      <ApiTokenBridge />
      {children}
    </ClerkProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ClerkWithRouter>
      <OrgIdProvider>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/sign-in/*" element={<SignIn />} />
        <Route path="/sign-up/*" element={<SignUp />} />
        <Route path={WORKSPACE_URL} element={<WorkspacePage />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<Layout />}>
            <Route index element={<Navigate to="/dashboard/overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="tasks/:key" element={<TasksPage />} />
            <Route path="action-items" element={<Navigate to="/dashboard/tasks" replace />} />
            <Route path="clusters" element={<Navigate to="/dashboard/search-performance?tab=topics" replace />} />
            <Route path="articles" element={<CmsGate feature="Articles" description="See every published article with its GSC clicks, AEO score, persona fit and rewrite suggestions."><Articles /></CmsGate>} />
            <Route path="articles/:id" element={<CmsGate feature="Articles" description="See every published article with its GSC clicks, AEO score, persona fit and rewrite suggestions."><ArticleDetailPage /></CmsGate>} />
            <Route path="articles/:id/outline" element={<CmsGate feature="Articles" description="See every published article with its GSC clicks, AEO score, persona fit and rewrite suggestions."><ArticleOutlinePage /></CmsGate>} />
            <Route path="cannibalization" element={<CmsGate feature="Cannibalization" description="Find articles that compete for the same topic, and which one to merge or redirect."><Cannibalization /></CmsGate>} />
            <Route path="graph" element={<KnowledgeGraph />} />
            <Route path="recommendations" element={<Recommendations />} />
            <Route path="linking" element={<CmsGate feature="Internal Linking" description="Get internal link suggestions between your articles, ready to apply."><Linking /></CmsGate>} />
            <Route path="writing" element={<Writing />} />
            <Route path="writing/:id" element={<OutlineEditor />} />
            <Route path="sitemap" element={<SitemapPage />} />
            <Route path="fanout" element={<FanoutPage />} />
            <Route path="query-map" element={<QueryMapPage />} />
            <Route path="search-performance" element={<SearchPerformancePage />} />
            <Route path="audience" element={<AudiencePage />} />
            <Route path="query-gaps" element={<QueryGapsPage />} />
            <Route path="ai-citations" element={<AiCitationsPage />} />
            <Route path="query-gaps/:queryId" element={<QueryDetailPage />} />
            <Route path="product-signals" element={<Navigate to="/dashboard/audience" replace />} />
            <Route path="audit" element={<TechnicalAuditPage />} />
            <Route path="audit/page/:pageId" element={<AuditPageDetailPage />} />
            <Route path="schema" element={<SchemaAuditPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="reports/templates" element={<ReportTemplatesPage />} />
            <Route path="reports/:id" element={<ReportDetailPage />} />
            <Route path="agents" element={<AgentSetupPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="settings" element={<Settings />}>
              <Route index element={<Navigate to="site" replace />} />
              <Route path="site" element={<SiteSection />} />
              <Route path="cms" element={<CmsListSection />} />
              <Route path="cms/:cmsId" element={<CmsDetailSection />} />
              <Route path="business" element={<BusinessSection />} />
              <Route path="search-console" element={<SearchConsoleSection />} />
              <Route path="indexing" element={<IndexingSection />} />
              <Route path="api-keys" element={<ApiKeysSection />} />
              <Route path="integrations" element={<IntegrationsSection />} />
              <Route path="*" element={<Navigate to="site" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard/overview" replace />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </OrgIdProvider>
      </ClerkWithRouter>
    </BrowserRouter>
  );
}
